/**
 * Top-bar search: Hubs and items the user can see, by title or description, plus
 * "Search Knowledgebase for …" (deep-links to /hubs/knowledgebase?q=…). A
 * combobox: ↑/↓ pick, Enter opens, Esc clears and closes.
 */
import React, { useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { DWA } from '../theme/dwaTokens';
import { findHub } from './hubs';
import { searchHubs } from './hubNav';
import type { SearchResult } from './hubNav';
import { SoonBadge } from './HubMenu';

export const GlobalSearch: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const listId = useId();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const results = useMemo(() => searchHubs(query, user), [query, user]);
  const showList = open && query.trim().length > 0;

  const choose = (r: SearchResult | undefined) => {
    if (!r || !r.route) return;
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
    navigate(r.route);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => (results.length ? (i + 1) % results.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (results.length ? (i - 1 + results.length) % results.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(results[active]);
    } else if (e.key === 'Escape') {
      if (query) setQuery('');
      setOpen(false);
    }
  };

  const iconFor = (r: SearchResult) => {
    if (r.kind === 'hub') return r.hub.icon;
    if (r.kind === 'kb') return faBook;
    return findHub(r.item.home)?.icon ?? faMagnifyingGlass;
  };

  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 0, maxWidth: 520 }}>
      <FontAwesomeIcon
        icon={faMagnifyingGlass}
        aria-hidden="true"
        style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: DWA.onDarkMuted }}
      />
      <input
        ref={inputRef}
        type="search"
        role="combobox"
        aria-label="Search Omega Wolf"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={showList && results[active] ? `${listId}-${results[active].id}` : undefined}
        placeholder="Search Hubs"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        onKeyDown={onKeyDown}
        style={{
          width: '100%',
          height: 36,
          padding: '0 0.75rem 0 2rem',
          borderRadius: 999,
          border: `1px solid rgba(255,255,255,0.16)`,
          background: 'rgba(255,255,255,0.07)',
          color: DWA.onDark,
          fontSize: '0.85rem',
          fontFamily: DWA.font,
          outline: 'none',
        }}
        className="ow-search-input"
      />
      {showList && (
        <ul
          id={listId}
          role="listbox"
          aria-label="Search results"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            minWidth: 'min(320px, calc(100vw - 2rem))',
            listStyle: 'none',
            margin: 0,
            padding: '0.35rem',
            background: '#ffffff',
            borderRadius: DWA.radius,
            boxShadow: DWA.cardShadow,
            border: `1px solid ${DWA.cardBorder}`,
            zIndex: 210,
            maxHeight: '70vh',
            overflowY: 'auto',
          }}
        >
          {results.length === 0 && (
            <li style={{ padding: '0.6rem 0.75rem', fontSize: '0.85rem', color: DWA.textSecondary }}>No matches.</li>
          )}
          {results.map((r, i) => {
            const disabled = !r.route;
            return (
              <li
                key={r.id}
                id={`${listId}-${r.id}`}
                role="option"
                aria-selected={i === active}
                aria-disabled={disabled || undefined}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(r)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.65rem',
                  padding: '0.5rem 0.65rem',
                  borderRadius: DWA.radius,
                  cursor: disabled ? 'default' : 'pointer',
                  background: i === active ? DWA.goldSoft : 'transparent',
                  opacity: disabled ? 0.6 : 1,
                  borderTop: r.kind === 'kb' && i > 0 ? `1px solid ${DWA.lineLight}` : undefined,
                }}
              >
                <FontAwesomeIcon icon={iconFor(r)} aria-hidden="true" style={{ width: 16, color: DWA.goldInk, fontSize: '0.85rem' }} />
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.875rem', fontWeight: 600, color: DWA.ink }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</span>
                    {r.kind === 'item' && r.comingSoon && <SoonBadge tone="light" />}
                  </span>
                  <span style={{ display: 'block', fontSize: '0.75rem', color: DWA.textSecondary }}>{r.subtitle}</span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
